// frontend/src/pages/BookingManagement.tsx
// MINIMAL UI FIX - Keep all existing functionality intact

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  RefreshCw,
  Grid,
  List,
  Filter
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useBookingsWithStats, useUpdateBookingStatus } from '@/hooks/useBookings';
import { BookingFilters as IBookingFilters, BookingStatus, BookingQueryParams } from '@/types/booking';

// Import new mobile-first components
import BookingStats from '@/components/Booking/BookingStats';
import BookingFilters from '@/components/Booking/BookingFilters';
import BookingList from '@/components/Booking/BookingList';
import BookingCard from '@/components/Booking/BookingCard';
import { Pagination, ErrorState, PageHeader, StatsGrid } from '@/components/common/ui';
import { Calendar } from 'lucide-react';

interface BookingManagementProps {
  context?: 'admin' | 'provider' | 'customer';
}

// Helper function to detect context from route
const detectContextFromRoute = (pathname: string, userRole?: string): 'admin' | 'provider' | 'customer' => {
  if (pathname.includes('/account/')) return 'customer';
  if (userRole === 'admin' || userRole === 'super_admin') return 'admin';
  if (userRole === 'provider') return 'provider';
  return 'customer';
};

// Helper function to get route base path
const getRouteBasePath = (context: string): string => {
  switch (context) {
    case 'customer': return '/account/bookings';
    case 'admin': return '/admin/bookings';
    case 'provider': return '/bookings';
    default: return '/bookings';
  }
};

const BookingManagement: React.FC<BookingManagementProps> = ({ 
  context 
}) => {
  // Auth and navigation
  const { user } = useAuth();
  const { status: urlStatus } = useParams<{ status?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Auto-detect context from route if not provided
  const detectedContext = context || detectContextFromRoute(location.pathname, user?.role);
  
  // State management
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<IBookingFilters>({
    search: '',
    status: 'all',
    date_from: '',
    date_to: ''
  });

  // Update filters when URL status changes
  useEffect(() => {
    if (urlStatus && ['pending', 'accepted', 'completed', 'rejected', 'cancelled'].includes(urlStatus)) {
      setFilters(prev => ({ ...prev, status: urlStatus as BookingStatus }));
    } else if (!urlStatus) {
      setFilters(prev => ({ ...prev, status: 'all' }));
    }
  }, [urlStatus]);

  // Build query parameters
  const queryParams: BookingQueryParams = {
    page,
    limit,
    search: filters.search,
    status: filters.status,
    date_from: filters.date_from,
    date_to: filters.date_to,
    ...(filters.provider_id && {
      provider_id: isNaN(Number(filters.provider_id)) ? filters.provider_id : Number(filters.provider_id)
    }),
    ...(filters.customer_id && {
      customer_id: isNaN(Number(filters.customer_id)) ? filters.customer_id : Number(filters.customer_id)
    }),
    ...(filters.service_type && { service_type: filters.service_type })
  };

  // Hooks
  const {
    bookings,
    pagination,
    stats,
    isLoading,
    hasError,
    refetchAll
  } = useBookingsWithStats(queryParams);

  const updateStatusMutation = useUpdateBookingStatus();

  // Event handlers
  const handleFilterChange = (newFilters: Partial<IBookingFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPage(1);
    
    if (newFilters.status !== undefined) {
      const basePath = getRouteBasePath(detectedContext);
      const newUrl = newFilters.status === 'all' ? basePath : `${basePath}/${newFilters.status}`;
      navigate(newUrl, { replace: true });
    }
  };

  const handleBookingAction = (action: string, bookingId: number) => {
    // Convert action string to status update
    if (action === 'accept') {
      handleStatusUpdate(bookingId, 'accepted');
    } else if (action === 'reject') {
      handleStatusUpdate(bookingId, 'rejected');
    } else if (action === 'complete') {
      handleStatusUpdate(bookingId, 'completed');
    }
  };

  const handleStatusUpdate = async (bookingId: number, status: BookingStatus, notes?: string) => {
    try {
      await updateStatusMutation.mutateAsync({
        bookingId,
        statusUpdate: { status, notes }
      });
    } catch (error) {
      console.error('Failed to update booking status:', error);
    }
  };

  const handleRefresh = () => {
    refetchAll();
  };


  // Check if filters are active
  const hasActiveFilters = filters.search || 
                          filters.status !== 'all' || 
                          filters.date_from || 
                          filters.date_to ||
                          filters.provider_id ||
                          filters.customer_id ||
                          filters.service_type;

  const clearAllFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      date_from: '',
      date_to: ''
    });
    setPage(1);
    const basePath = getRouteBasePath(detectedContext);
    navigate(basePath, { replace: true });
  };

  // Get page title based on context
  const getPageTitle = () => {
    switch (detectedContext) {
      case 'admin': return 'All Bookings';
      case 'provider': return 'My Service Requests';
      case 'customer': return 'My Bookings';
      default: return 'Bookings';
    }
  };

  // Error state
  if (hasError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ErrorState
          type="server"
          title="Error Loading Bookings"
          message="Failed to load booking data. Please try again."
          onRetry={handleRefresh}
          size="lg"
          fullHeight
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Simple Page Title - Just text, no card */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{getPageTitle()}</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your service requests and bookings
          </p>
        </div>

        {/* Stats as Info Section - No card wrapper */}
        <div className="mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Overview</h2>
          <BookingStats
            stats={stats || {
              total: 0,
              pending: 0,
              accepted: 0,
              completed: 0,
              rejected: 0,
              cancelled: 0
            }}
            currentFilter={filters.status}
            onFilterChange={(status) => handleFilterChange({ status: status as BookingStatus | 'all' })}
            showAdminStats={detectedContext === 'admin'}
          />
        </div>

        {/* Filters Section - Outside card */}
        {showFilters && (
          <div className="mb-6">
            <BookingFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              isLoading={isLoading}
              showAdminFilters={detectedContext === 'admin'}
            />
          </div>
        )}

        {/* Actions - Below filters, above listing */}
        <div className="mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium ${
                hasActiveFilters 
                  ? 'text-white bg-blue-600 border-blue-600 hover:bg-blue-700' 
                  : 'text-gray-700 bg-white hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4 mr-2" />
              Toggle Filters
              {hasActiveFilters && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white text-blue-600">
                  {Object.values(filters).filter(v => v && v !== 'all').length}
                </span>
              )}
            </button>
            <button
              onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              {viewMode === 'list' ? <Grid className="h-4 w-4 mr-2" /> : <List className="h-4 w-4 mr-2" />}
              {viewMode === 'list' ? 'Grid View' : 'List View'}
            </button>
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {pagination && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                {pagination.total || bookings?.length || 0} total
              </span>
            )}
          </div>
        </div>

        {/* Mobile: No card wrapper for max space */}
        <div className="block xl:hidden">
          {/* Mobile Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Bookings</h3>
            {pagination && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                {pagination.total || bookings?.length || 0} total
              </span>
            )}
          </div>
          {viewMode === 'list' ? (
            <BookingList
              bookings={bookings}
              currentUserRole={detectedContext}
              isLoading={isLoading}
              emptyMessage={
                hasActiveFilters 
                  ? "No bookings match your current filters. Try adjusting your search criteria."
                  : "No bookings found. Your bookings will appear here once you start receiving requests."
              }
            />
          ) : (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {bookings.map((booking: any) => (
                  <BookingCard
                    key={booking.booking_id}
                    booking={booking}
                    currentUserRole={detectedContext}
                    onAction={handleBookingAction}
                    compact={false}
                  />
                ))}
              </div>
              {bookings.length === 0 && !isLoading && (
                <div className="text-center py-8">
                  <p className="text-gray-500">
                    {hasActiveFilters 
                      ? "No bookings match your current filters." 
                      : "No bookings found."}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Desktop: Card wrapper for clean look */}
        <div className="hidden xl:block bg-white rounded-lg border border-gray-200 shadow-sm">
          {/* Card Header with count */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Bookings</h3>
              {pagination && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {pagination.total || bookings?.length || 0} total
                </span>
              )}
            </div>
          </div>

          {/* Bookings Listing */}
          <div className="p-6">
            {viewMode === 'list' ? (
              <BookingList
                bookings={bookings}
                currentUserRole={detectedContext}
                isLoading={isLoading}
                emptyMessage={
                  hasActiveFilters 
                    ? "No bookings match your current filters. Try adjusting your search criteria."
                    : "No bookings found. Your bookings will appear here once you start receiving requests."
                }
              />
            ) : (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {bookings.map((booking: any) => (
                    <BookingCard
                      key={booking.booking_id}
                      booking={booking}
                      currentUserRole={detectedContext}
                      onAction={handleBookingAction}
                      compact={false}
                    />
                  ))}
                </div>
                {bookings.length === 0 && !isLoading && (
                  <div className="text-center py-8">
                    <p className="text-gray-500">
                      {hasActiveFilters 
                        ? "No bookings match your current filters." 
                        : "No bookings found."}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Pagination - Show if we have pagination data and more than 1 page OR if we have bookings */}
        {(pagination && (pagination.total_pages > 1 || pagination.total > 0)) && (
          <div className="flex justify-center mt-6">
            <div className="flex flex-col items-center space-y-2">
              {/* Debug info - remove in production */}
              {process.env.NODE_ENV === 'development' && (
                <div className="text-xs text-gray-500 bg-yellow-50 p-2 rounded border">
                  Debug: Page {pagination.current_page || 1} of {pagination.total_pages || 1}, Total: {pagination.total || 0}
                  <br/>
                  State page: {page}, hasNext: {pagination.current_page < pagination.total_pages ? 'true' : 'false'}
                  <br/>
                  Pagination object: {JSON.stringify(pagination)}
                </div>
              )}
              
              <Pagination
                currentPage={pagination.current_page || 1}
                totalPages={pagination.total_pages || 1}
                totalItems={pagination.total || 0}
                itemsPerPage={pagination.per_page || 10}
                onPageChange={(newPage) => {
                  console.log(`Pagination clicked: changing from page ${page} to page ${newPage}`);
                  setPage(newPage);
                }}
                variant="default"
                size="md"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingManagement;